import * as React from 'react';
import { useMemo, useState } from 'react';

import { X } from 'lucide-react';

import { Button } from 'components/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { TMSEquipmentType } from 'types/Load';
import { MaybeUndef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { cn } from 'utils/shadcn';

import { resolveSpecialReferenceCode } from './utils';

function specialReferencePlaceholder({
  customerSelected,
  isLoading,
  hasOptions,
  placeholder,
}: {
  customerSelected: boolean;
  isLoading: boolean;
  hasOptions: boolean;
  placeholder: string;
}): string {
  if (!customerSelected) {
    return 'Select a customer first';
  }
  if (isLoading) {
    return 'Loading…';
  }
  if (!hasOptions) {
    return 'No options found';
  }

  return placeholder;
}

type SpecialReferenceSelectProps = {
  value: string;
  options: MaybeUndef<TMSEquipmentType[]>;
  isLoading?: boolean;
  customerSelected: boolean;
  ariaLabel: string;
  placeholder?: string;
  onChange: (code: string) => void;
  className?: string;
};

export default function SpecialReferenceSelect({
  value,
  options,
  isLoading = false,
  customerSelected,
  ariaLabel,
  placeholder = 'Select…',
  onChange,
  className,
}: SpecialReferenceSelectProps): React.JSX.Element {
  const [selectEpoch, setSelectEpoch] = useState(0);
  const selectedCode = resolveSpecialReferenceCode(value, options);

  const displayOptions = useMemo(() => {
    const list = options ?? [];
    if (!selectedCode) {
      return list;
    }
    const alreadyListed = list.some(
      (option) => option.id?.trim().toLowerCase() === selectedCode.toLowerCase()
    );
    if (alreadyListed) {
      return list;
    }

    return [{ id: selectedCode, name: value.trim() || selectedCode }, ...list];
  }, [options, selectedCode, value]);

  const clearSelection = () => {
    setSelectEpoch((epoch) => epoch + 1);
    onChange('');
  };

  const selectPlaceholder = specialReferencePlaceholder({
    customerSelected,
    isLoading,
    hasOptions: displayOptions.length > 0,
    placeholder,
  });

  return (
    <Flex align='start' gap='xs' className={cn('w-full', className)}>
      <div className='min-w-0 flex-1'>
        <Select
          key={selectEpoch}
          value={selectedCode || undefined}
          onValueChange={onChange}
          disabled={
            !customerSelected ||
            (isLoading && displayOptions.length === 0 && !selectedCode)
          }
        >
          <SelectTrigger className='mt-1 text-[13px]' aria-label={ariaLabel}>
            <SelectValue placeholder={selectPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {displayOptions.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.name || option.id}
              </SelectItem>
            ))}
            {displayOptions.length === 0 && (
              <div className='px-2 py-3'>
                <Typography variant='body-xs' textColor='muted'>
                  {isLoading ? 'Loading…' : 'No options found'}
                </Typography>
              </div>
            )}
          </SelectContent>
        </Select>
      </div>
      {value ? (
        <Button
          type='button'
          variant='ghost'
          size='xs'
          buttonNamePosthog={ButtonNamePosthog.MGBSpecialReferenceCleared}
          className='mt-1 shrink-0'
          aria-label={`Clear ${ariaLabel}`}
          onClick={clearSelection}
        >
          <X className='h-4 w-4' />
        </Button>
      ) : null}
    </Flex>
  );
}
